import turfBbox from '@turf/bbox'
import { mapIntelligenceActions } from 'redux/mapIntelligence/mapIntelligenceSlice'
import Loading from 'core-system/Loading'
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppState } from 'redux/config/store'
import { MapLayers } from 'redux/mapIntelligence/mapIntelligenceTypes'
import styled from 'styled-components'
import Grid from '@mui/material/Grid2'
import MapIntelligence from 'features/MapIntelligence/MapIntelligence'
import ControlPanel from './components/ControlPanel'
import MapIntelligenceKeyMetrics from 'features/MapIntelligence/MapIntelligenceKeyMetrics'
import StatsTable from './widgets/StatsTable'
import ProgramRecommender from 'features/ProgramRecommender/widgets/ProgramRecommender'
import Card from 'core-system/Card'
import { getProgramRecommenderStats } from './ProgramRecommenderService'
import { Segment, Worksite } from 'redux/employer/employerTypes'
import { getCityCosts } from './cityCost'
import AiInsights from 'features/ProgramRecommender/widgets/AiInsights'
import Tabs from 'core-system/Tabs/Tabs'
import HighestImpactWorksites from './widgets/HighestImpactWorksites'
import ComplianceCard from './widgets/ComplianceCard'
import PageWrapper from './components/PageWrapper'
import Box from './components/Box'
import SegmentOverview from './widgets/SegmentOverview'
import { mockComplianceData } from './mockData'
import { WorksiteData, ProgramRecommenderStats, ActiveLayer } from './types'
import { getReach, calculateYearlyCarbonImpact, getStats } from './Utils'

const TabsCardContainer = styled(Card)`
  margin-top: 0 !important;
  margin-bottom: 1rem;
  padding: 0rem 1.25rem 0rem 1.25rem;
`

const getSegmentCity = (name: string) => {
  switch (name) {
    case 'RTO - SF':
      return 'San Francisco'
    case 'RTO - NYC':
      return 'New York'
    case 'RTO - SEA':
      return 'Seattle'
    default:
      return 'Other (Default)'
  }
}

const getCityAndEmployeeCount = (
  selectedWorksite: string,
  worksites,
  allSegments
) => {
  const data = {
    city: 'Other (Default)',
    employeeCount: (worksites || []).reduce(
      (total, worksite) => total + (worksite.employeeCount || 0),
      0
    ),
  }
  try {
    const selectedWorksiteData = selectedWorksite
      ? worksites.find((ws) => ws.id === selectedWorksite)
      : null
    if (selectedWorksiteData) {
      data.city = selectedWorksiteData.city
      data.employeeCount = selectedWorksiteData.employeeCount
    } else {
      // its a segment
      const selectedSegmentData = allSegments.find(
        (segment) => segment.id === selectedWorksite
      )
      if (selectedSegmentData) {
        data.city = getSegmentCity(selectedSegmentData.name)
        data.employeeCount = selectedSegmentData.commuters
      }
    }
    return data
  } catch (err) {
    return data
  }
}

const initialWorksiteData: WorksiteData = {
  micromobilityEmployeesReach: 0,
  transitEmployeesReach: 0,
  carEmployeesReach: 0,
  totalJourneys: 0,
  totalEmployees: 0,
  carbonImpact: {
    transitImpact: 0,
    micromobilityImpact: 0,
    carImpact: 0,
  },
  city: 'Other (Default)',
  worksiteName: 'All Employees',
  worksiteAddress: 'All Employees',
  worksiteId: 'all-employees',
}

const MapIntelligenceView = React.memo(() => {
  const dispatch = useDispatch()

  const { employeesGeojson, worksiteGeojson, agonyGeojson, statistics } =
    useSelector((state: AppState) => state.mapIntelligence)

  const { allEmployeeSegmentId } = useSelector(
    (state: AppState) => state.employer
  )
  const worksites =
    useSelector((state: AppState) => state.employer.worksites) || []
  const allSegments =
    useSelector((state: AppState) => state.employer.allSegments) || []
  // Getting groups out of allSegments
  const groups = allSegments.filter(
    (segment: Segment) => segment.type === 'CUSTOM'
  )
  const [activeLayer, setActiveLayer] = useState<ActiveLayer>('commuteModes')
  const [selectedWorksite, setSelectedWorksite] = useState<string | null>(null)
  const [daysInOffice, setDaysInOffice] = useState(2)
  const [programRecommenderData, setProgramRecommenderData] =
    useState<ProgramRecommenderStats>({
      worksites: {},
      highestImpactWorksites: [],
      aiInsights: [],
      allEmployeeStats: initialWorksiteData,
    })

  // Add state for tab management
  const [activeTab, setActiveTab] = useState(0)

  const { city, employeeCount } = getCityAndEmployeeCount(
    selectedWorksite,
    worksites,
    allSegments
  )

  const stats = getStats(selectedWorksite, programRecommenderData)

  const getProgramRecommenderData = async () => {
    try {
      const data = await getProgramRecommenderStats()
      if (data) {
        setProgramRecommenderData(data)
      }
    } catch (error) {}
  }

  useEffect(() => {
    getProgramRecommenderData()
  }, [])

  useEffect(() => {
    dispatch(
      mapIntelligenceActions.getBaseCommuters({
        segmentId: allEmployeeSegmentId,
      })
    )
    dispatch(
      mapIntelligenceActions.getAgonyData({
        segmentId: allEmployeeSegmentId,
      })
    )
  }, [dispatch, allEmployeeSegmentId])

  const handleWorksiteChange = (worksiteId: string | null) => {
    setSelectedWorksite(worksiteId)
  }

  const baseBbox = useMemo(
    () => (employeesGeojson ? turfBbox(employeesGeojson) : null),
    [employeesGeojson]
  )

  if (!employeesGeojson || !baseBbox || !agonyGeojson) {
    return <Loading fullPage={true} />
  }

  const mapLayers: MapLayers = {
    employees: employeesGeojson,
    agony: agonyGeojson,
    worksites: worksiteGeojson,
  }

  const carReach = getReach('carEmployeesReach', stats)
  const micromobilityReach = getReach('micromobilityEmployeesReach', stats)
  const transitReach = getReach('transitEmployeesReach', stats)

  const dropdownItems = [
    { id: '', text: 'All Employees' },
    ...worksites.map((worksite: Worksite) => ({
      id: worksite.id,
      text: worksite.alias || worksite.formattedAddress,
    })),
    ...groups.map((group: Segment) => ({
      id: group.id,
      text: group.name,
    })),
  ]

  const selectedWorksiteName =
    dropdownItems.find((item) => item.id === selectedWorksite)?.text ||
    'All Employees'

  const tabs = [
    { label: 'Commute Analysis', value: 0 },
    { label: 'Compliance', value: 1 },
    { label: 'Program Recs', value: 2 },
  ]

  const renderTabContent = () => {
    switch (activeTab) {
      case 0: // Commute Analysis
        return (
          <>
            <AiInsights insights={programRecommenderData.aiInsights} />
            <HighestImpactWorksites
              worksites={programRecommenderData.highestImpactWorksites}
            />
          </>
        )
      case 1: // Compliance
        return (
          <>
            {mockComplianceData.map((complianceData) => (
              <ComplianceCard {...complianceData} />
            ))}
          </>
        )
      case 2: // Program Recs
        return (
          <ProgramRecommender
            carReach={carReach}
            transitReach={transitReach}
            cityCosts={getCityCosts(city, daysInOffice)}
            selectedWorksiteName={selectedWorksiteName}
          />
        )
      default:
        return null
    }
  }

  return (
    <>
      <ControlPanel
        updateActiveLayer={setActiveLayer}
        activeLayer={activeLayer}
        selectedWorksite={selectedWorksite}
        onWorksiteChange={handleWorksiteChange}
        dropdownItems={dropdownItems}
      />
      <PageWrapper>
        <SegmentOverview
          carReach={carReach}
          micromobilityReach={micromobilityReach}
          transitReach={transitReach}
          getCityCosts={getCityCosts}
          selectedWorksiteName={selectedWorksiteName}
          selectedWorksiteCity={city}
          numberOfEmployees={employeeCount}
          daysInOffice={daysInOffice}
          onDaysInOfficeChange={setDaysInOffice}
          carbonImpact={calculateYearlyCarbonImpact(
            stats.carbonImpact,
            daysInOffice
          )}
        />
        <StatsTable
          carReach={carReach}
          micromobilityReach={micromobilityReach}
          transitReach={transitReach}
          getCityCosts={getCityCosts}
          selectedWorksiteCity={city}
          numberOfEmployees={employeeCount}
          daysInOffice={daysInOffice}
          carbonImpact={calculateYearlyCarbonImpact(
            stats.carbonImpact,
            daysInOffice
          )}
        />
        <Grid container spacing={2} style={{ marginBottom: '1rem' }}>
          <Grid size={{ xs: 12, md: 7 }}>
            <Box style={{ position: 'relative', minHeight: '600px' }}>
              <MapIntelligence
                mapLayers={mapLayers}
                activeLayer={activeLayer}
                statistics={statistics}
                selectedWorksite={selectedWorksite}
                worksites={worksites || []}
              />
            </Box>
            <MapIntelligenceKeyMetrics
              carReach={carReach}
              micromobilityReach={micromobilityReach}
              transitReach={transitReach}
              activeLayer={activeLayer}
              statistics={statistics}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <TabsCardContainer>
              <Tabs
                value={activeTab}
                onChange={(_, value) => setActiveTab(value)}
                aria-label='map intelligence tabs'
                tabs={tabs}
              />
            </TabsCardContainer>
            {renderTabContent()}
          </Grid>
        </Grid>
      </PageWrapper>
    </>
  )
})

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  MapIntelligenceView.displayName = 'MapIntelligenceView'
}

export default MapIntelligenceView
